// Phase RV-3 — Chain Archive (Table 18).
//
// Lists all rollover chains for the authenticated user. Each row links
// to /rollover/:id which renders the same RolloverPage parameterised
// by chain id.
//
// Visuals: Football Products (FpKit) Kalshi-clean — inline FpKit tokens,
// hairline borders, mono uppercase labels, neutral surfaces, no gradients.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FpHead } from "../football/_fpkit.tsx";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { getBrowserClient } from "../../supabase/browser.ts";

interface ArchiveRow {
  id: string;
  cadence: string;
  status: string;
  current_week: number;
  total_rollovers: number;
  current_value: number;
  opening_stake: number;
  odds_target: number | null;
  created_at: string;
  updated_at: string;
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function asNum(v: unknown, fb = 0): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : fb;
  }
  return fb;
}

function asStr(v: unknown, fb = ""): string {
  return typeof v === "string" ? v : fb;
}

// Status → outcome tone. Win/loss colours appear on text + faint soft
// badge backgrounds only; neutral statuses stay on tertiary text.
function statusTone(status: string): { fg: string; bg: string } {
  if (status === "active") {
    return { fg: "var(--fp-accent-text)", bg: "var(--fp-accent-soft)" };
  }
  if (status === "complete" || status === "completed") {
    return { fg: "var(--fp-won)", bg: "var(--fp-won-soft)" };
  }
  if (status === "busted" || status === "failed") {
    return { fg: "var(--fp-lost)", bg: "var(--fp-lost-soft)" };
  }
  return { fg: "var(--text-tertiary)", bg: "var(--bg-inset)" };
}

const MUTED_NOTE: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  color: "var(--text-tertiary)",
  marginTop: 12,
};

const EYEBROW_STYLE: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.14em",
  color: "var(--fp-accent-text)",
};

const PILL_STYLE: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "var(--text-secondary)",
  background: "var(--bg-inset)",
  border: "1px solid var(--border-faint)",
  borderRadius: 999,
  padding: "3px 9px",
  whiteSpace: "nowrap",
};

function StatBlock({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          color: "var(--text-tertiary)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: 18,
          lineHeight: 1,
          marginTop: 3,
          color: "var(--text)",
          letterSpacing: "0.01em",
        }}
      >
        {value}
      </div>
    </div>
  );
}

export function RolloverArchivePage(): JSX.Element {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user, loading: authLoading } = useAuthSession();
  const [rows, setRows] = useState<ArchiveRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;
    const res = await sb
      .from("rollover_chains")
      .select(
        "id, cadence, status, current_week, total_rollovers, current_value, opening_stake, odds_target, created_at, updated_at",
      )
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    if (res.error) {
      setError(res.error.message);
      setRows([]);
      setLoading(false);
      return;
    }
    const norm = ((res.data ?? []) as Array<Record<string, unknown>>).map((r) => ({
      id: asStr(r.id),
      cadence: asStr(r.cadence, "weekly"),
      status: asStr(r.status, "active"),
      current_week: asNum(r.current_week, 1),
      total_rollovers: asNum(r.total_rollovers, 0),
      current_value: asNum(r.current_value, 0),
      opening_stake: asNum(r.opening_stake, 0),
      odds_target: typeof r.odds_target === "number" ? r.odds_target : null,
      created_at: asStr(r.created_at),
      updated_at: asStr(r.updated_at),
    }));
    setRows(norm);
    setLoading(false);
  }, [supabase, user]);

  useEffect(() => {
    if (authLoading) return;
    void refetch();
  }, [authLoading, refetch]);

  return (
    <div className="fp" data-screen-label="03 ROLLOVER ARCHIVE">
      <FpHead
        kicker="ROLLOVER"
        title="ROLLOVER CHAINS"
        status="Archive"
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 14,
          marginTop: 10,
          marginBottom: 4,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            color: "var(--text-secondary)",
          }}
        >
          Every chain you have run. Tracker data is preserved permanently.
        </div>
        <Link
          to="/rollover"
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "var(--fp-accent-text)",
            textDecoration: "none",
            whiteSpace: "nowrap",
          }}
        >
          ← Active chain
        </Link>
      </div>

      <div
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: 10,
          padding: 14,
          marginTop: 10,
        }}
      >
        <div style={EYEBROW_STYLE}>Archive</div>
        {loading ? (
          <div style={MUTED_NOTE}>Loading chains…</div>
        ) : error ? (
          <div style={MUTED_NOTE}>Archive unavailable. {error}</div>
        ) : rows.length === 0 ? (
          <div style={MUTED_NOTE} data-test="archive-empty">
            No chains yet. Start a chain from the Active screen to see it here.
          </div>
        ) : (
          <div style={{ marginTop: 8 }}>
            {rows.map((r, idx) => {
              const shortId = r.id.slice(-6).toUpperCase();
              const endLabel = r.status === "active" ? "ACTIVE" : shortDate(r.updated_at);
              const tone = statusTone(r.status);
              return (
                <div
                  key={r.id}
                  style={{
                    borderTop: idx === 0 ? "none" : "1px solid var(--border-faint)",
                    padding: "12px 0",
                  }}
                >
                  <Link
                    to={`/rollover/${r.id}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      flex: 1,
                      textDecoration: "none",
                      color: "inherit",
                    }}
                  >
                    <div style={{ minWidth: 110 }}>
                      <div
                        style={{
                          fontFamily: "'Bebas Neue', sans-serif",
                          fontSize: 15,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          color: "var(--text)",
                          lineHeight: 1,
                        }}
                      >
                        Chain #{shortId}
                      </div>
                      <div
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 10,
                          color: "var(--text-tertiary)",
                          marginTop: 4,
                          letterSpacing: "0.06em",
                        }}
                      >
                        {shortDate(r.created_at)} → {endLabel}
                      </div>
                    </div>
                    <span style={PILL_STYLE}>{r.cadence.toUpperCase()}</span>
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        color: tone.fg,
                        background: tone.bg,
                        borderRadius: 999,
                        padding: "3px 9px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {r.status.toUpperCase()}
                    </span>
                    <div
                      style={{
                        flex: 1,
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
                        gap: 14,
                      }}
                    >
                      <StatBlock
                        label="Progress"
                        value={`${r.current_week} / ${r.total_rollovers}`}
                      />
                      <StatBlock label="Seed Stake" value={`GH₵${r.opening_stake.toFixed(2)}`} />
                      <StatBlock label="Peak Value" value={`GH₵${r.current_value.toFixed(2)}`} />
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
